// Reusable Stimulus controller for auto-refreshing Turbo Frames
// Preserves current URL query parameters during refresh
//
// Polls on an interval (with visibility-aware pause/resume, ETag/content-hash
// de-duplication, and sessionStorage caching). For a lighter one-shot reload
// triggered by an external event instead of a timer, see frame_reloader_controller.js.
//
// Usage:
// <div data-controller="frame-refresh"
//      data-frame-refresh-interval-value="30000"
//      data-frame-refresh-preserve-params-value="true"
//      data-frame-refresh-cache-value="true"
//      data-action="visibilitychange@document->frame-refresh#handleVisibilityChange
//                   turbo:frame-load->frame-refresh#saveCachedContent">
//   <%= turbo_frame_tag "my_frame", data: { frame_refresh_target: "frame" } do %>
//     ...
//   <% end %>
// </div>
import { Controller } from "@hotwired/stimulus"
import Logger from '../lib/logger'

export default class extends Controller {
  static values = {
    interval: { type: Number, default: 30000 },      // 30 seconds default
    preserveParams: { type: Boolean, default: true }, // Keep URL params
    url: { type: String, default: "" },               // Optional: explicit URL to refresh
    cache: { type: Boolean, default: false },        // Enable caching
    cacheTtl: { type: Number, default: 300000 }      // 5 minutes default
  }

  static targets = ["form"]  // Optional: filter form target

  connect() {
    this.lastEtag = null
    if (this.cacheValue) this.loadCachedContent()


    // Initialize hash with current content to prevent first-refresh mismatches
    const frame = this.getFrame()
    if (frame) {
      this.lastContentHash = this.calculateContentHash(frame.innerHTML)
    }

    // Trigger immediate refresh to ensure content is loaded/updated
    // This acts as "stale-while-revalidate" and fixes issues where Turbo's native src fetch
    // might be interrupted or fail to render initially.
    this.refresh()
  }

  disconnect() {
    this.stopRefresh()
  }

  // Stimulus Value Change Callbacks
  // Triggered automatically when value changes (and on initialization)

  intervalValueChanged() {
    if (this.hasConnected) {
      this.stopRefresh()
      this.startRefresh()
    } else {
      // On initial connect, start refresh if not already started by another value callback
      this.startRefresh()
    }
  }

  startRefresh() {
    if (this.refreshTimer) return
    this.refreshTimer = setInterval(() => this.refresh(), this.intervalValue)
  }

  stopRefresh() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer)
      this.refreshTimer = null
    }
  }

  // Public action - triggered via data-action="visibilitychange@document->frame-refresh#handleVisibilityChange"
  handleVisibilityChange() {
    if (document.hidden) {
      this.stopRefresh()
    } else {
      this.refresh() // Refresh immediately when tab becomes visible
      this.startRefresh()
    }
  }

  // Public action - can be triggered manually via data-action="click->frame-refresh#refresh"
  async refresh() {
    const frame = this.getFrame()
    if (!frame) return

    const url = this.buildRefreshUrl()

    try {
      const response = await fetch(url, {
        headers: {
          "Accept": "text/html, application/xhtml+xml",
          "Turbo-Frame": frame.id,
          "X-Requested-With": "XMLHttpRequest"
        }
      })

      // Browser often transparently handles 304 and returns 200 + Cached Body
      // So we MUST check ETag manually to see if it actually changed
      const newEtag = response.headers.get("ETag")

      if (newEtag && newEtag === this.lastEtag) {
        return
      }

      // If server explicitly returns 304 (rare with Fetch API)
      if (response.status === 304) {
        return
      }

      if (response.ok) {
        // Update our ETag reference
        if (newEtag) this.lastEtag = newEtag

        const html = await response.text()
        const parser = new DOMParser()
        const doc = parser.parseFromString(html, "text/html")
        const newFrame = doc.getElementById(frame.id)

        if (newFrame) {
          // Double-Check: Content Hashing
          // If ETag fails or is missing (Safari), we compare the actual content hash.
          // This ensures we NEVER overwrite the DOM if the content is identical.
          const newContent = newFrame.innerHTML
          const newHash = this.calculateContentHash(newContent)

          if (this.lastContentHash && newHash === this.lastContentHash) {
            return
          }

          this.lastContentHash = newHash
          frame.innerHTML = newContent
          // Trigger Turbo event so our caching logic (and others) can react
          frame.dispatchEvent(new CustomEvent("turbo:frame-load", { bubbles: true }))
        }
      }
    } catch (e) {
      Logger.warn("Refresh failed:", e)
    }
  }

  getFrame() {
    // If the controller is attached directly to the frame
    if (this.element.tagName === "TURBO-FRAME") return this.element

    // Fallback: find child frame
    return this.element.querySelector("turbo-frame")
  }

  buildRefreshUrl() {
    // Strategy 1: Explicit URL provided
    if (this.urlValue) {
      return this.urlValue
    }

    // Strategy 2: If we have a form target, use its current values
    // This is the most reliable source for state
    if (this.hasFormTarget) {
      const formData = new FormData(this.formTarget)
      const searchParams = new URLSearchParams(formData)

      // Use the form's action as base
      const baseUrl = this.formTarget.action
      // Ensure we don't double-stack params if action has them, though usually action is clean path
      const cleanBase = baseUrl.split('?')[0]

      return `${cleanBase}?${searchParams.toString()}`
    }

    // Strategy 3: Frame existing SRC
    const frame = this.getFrame()
    if (frame && frame.src) {
      // Just use the frame's source as is
      return frame.src
    }

    return ""
  }

  // Caching Methods

  get storageKey() {
    // Simple robust key: prefix + frame_id + (hashed url/params)
    const frame = this.getFrame()
    const id = this.element.id || (frame ? frame.id : "frame")

    let suffix = ""
    if (this.urlValue) suffix = this.urlValue
    else if (frame && frame.src) suffix = frame.src

    // Simple hash of suffix to keep key valid/short
    if (suffix) {
      const hash = suffix.split('').reduce((a, b) => { a = ((a << 5) - a) + b.charCodeAt(0); return a & a }, 0)
      suffix = `_${hash}`
    }

    return `frame_cache_${id}${suffix}`
  }

  loadCachedContent() {
    try {
      const cached = sessionStorage.getItem(this.storageKey)
      if (!cached) return

      const data = JSON.parse(cached)
      if (Date.now() - data.timestamp > this.cacheTtlValue) {
        sessionStorage.removeItem(this.storageKey)
        return
      }

      const frame = this.getFrame()
      if (frame) {
        frame.innerHTML = data.html
      }
    } catch (e) {
      Logger.warn("Error loading cached content:", e)
    }
  }

  // Public action - triggered via data-action="turbo:frame-load->frame-refresh#onFrameLoad"
  onFrameLoad() {
    const frame = this.getFrame()
    if (frame) {
      this.lastContentHash = this.calculateContentHash(frame.innerHTML)
    }

    if (this.cacheValue) {
      try {
        if (!frame) return
        const data = {
          html: frame.innerHTML,
          timestamp: Date.now()
        }
        sessionStorage.setItem(this.storageKey, JSON.stringify(data))
      } catch (e) {
        // Quota exceeded or disabled
      }
    }
  }

  // Helper to prevent redundant timer starts during connect/value change overlap
  get hasConnected() {
    return this.element.isConnected
  }

  // Robust hashing that ignores dynamic CSRF tokens which cause false-positives
  calculateContentHash(htmlString) {
    if (!htmlString) return 0

    // Parse HTML to manipulate DOM safe from regex errors
    const parser = new DOMParser()
    const doc = parser.parseFromString(htmlString, "text/html")

    // Remove authenticity tokens which change on every render
    const tokens = doc.querySelectorAll('input[name="authenticity_token"]')
    tokens.forEach(token => token.remove())

    // Clear dynamic content (e.g. timestamps) marked to be ignored
    const ignored = doc.querySelectorAll("[data-ignore-hash]")
    ignored.forEach(el => el.innerHTML = "")

    // Hash the cleaned content, ignoring whitespace to prevent false positives
    // (Browser serialization vs Server text often differ in whitespace/indentation)
    const cleanContent = doc.body.innerHTML.replace(/\s+/g, "")

    return cleanContent.split('').reduce((a, b) => { a = ((a << 5) - a) + b.charCodeAt(0); return a & a }, 0)
  }
}